import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useInvoices } from "@/context/InvoiceContext";
import { useEnhancedInvoices } from "@/context/EnhancedInvoiceContext";
import { Invoice } from "@/types/stock";
import { EnhancedInvoice, PaymentStatus } from "@/types/invoice";
import {
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  FileText,
  Download,
  Eye,
  Send,
  DollarSign,
  CheckSquare,
  Mail,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import InvoiceFormDialog from "./InvoiceFormDialog";
import InvoiceViewDialog from "./InvoiceViewDialog";
import StockPasswordDialog from "../stock/StockPasswordDialog";
import PaymentRecordForm from "./PaymentRecordForm";
import { Checkbox } from "@/components/ui/checkbox";
import { ClientManagement } from "@/components/clients/ClientManagement";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Layout } from "lucide-react";
import { CreditManagementService } from "@/services/CreditManagementService";
import InvoicePDFDownload from "./InvoicePDFDownload";

const InvoiceManagement: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { invoices, createInvoice, updateInvoice, deleteInvoice, loading } =
    useInvoices();
  const {
    enhancedInvoices,
    createPaymentRecord,
    updateOverdueInvoices,
    refreshInvoices: refreshEnhancedInvoices,
    loading: enhancedLoading,
  } = useEnhancedInvoices();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedPaymentStatus, setSelectedPaymentStatus] =
    useState<string>("all");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);
  const [paymentInvoice, setPaymentInvoice] = useState<EnhancedInvoice | null>(
    null
  );
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<() => void>(() => {});
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(
    new Set()
  );
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Filter invoices based on search term, status, and payment status
  const filteredInvoices = enhancedInvoices.filter((invoice) => {
    const matchesSearch =
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.clientPhone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.notes?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      selectedStatus === "all" || invoice.status === selectedStatus;

    const matchesPaymentStatus =
      selectedPaymentStatus === "all" ||
      invoice.paymentStatus === selectedPaymentStatus;

    return matchesSearch && matchesStatus && matchesPaymentStatus;
  });

  const handleCreateInvoice = () => {
    setPendingAction(() => () => {
      setEditingInvoice(null);
      setIsFormOpen(true);
    });
    setIsPasswordDialogOpen(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setPendingAction(() => () => {
      setEditingInvoice(invoice);
      setIsFormOpen(true);
    });
    setIsPasswordDialogOpen(true);
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setViewingInvoice(invoice);
    setIsViewOpen(true);
  };

  const handleDeleteInvoice = async (invoice: Invoice) => {
    setPendingAction(() => async () => {
      try {
        await deleteInvoice(invoice.id);
        toast({
          title: t("invoices.deleted"),
          description: t("invoices.deletedDescription"),
        });
      } catch (error) {
        toast({
          title: t("common.error"),
          description: t("invoices.deleteError"),
          variant: "destructive",
        });
      }
    });
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordSuccess = () => {
    setIsPasswordDialogOpen(false);
    pendingAction();
  };

  const handleRecordPayment = (invoice: EnhancedInvoice) => {
    setPaymentInvoice(invoice);
    setIsPaymentFormOpen(true);
  };

  const handleStatusChange = async (invoice: Invoice, newStatus: string) => {
    console.log("Changing invoice status:", {
      invoiceId: invoice.id,
      currentStatus: invoice.status,
      newStatus,
    });
    try {
      // Direct database update using supabase
      const dbUpdateData: any = {
        status: newStatus,
        updated_at: new Date().toISOString(),
      };

      // When marking as paid, also update payment status
      if (newStatus === "paid") {
        dbUpdateData.payment_status = "paid";
        dbUpdateData.paid_amount = invoice.totalAmount;
        dbUpdateData.remaining_amount = 0;
      }

      const { error } = await supabase
        .from("invoices")
        .update(dbUpdateData)
        .eq("id", invoice.id);

      if (error) throw error;

      console.log("Invoice status updated successfully");

      // Refresh the enhanced invoices data to reflect the changes
      await refreshEnhancedInvoices();

      // Recalculate client credit if the invoice has a client
      if (invoice.clientId) {
        console.log("Recalculating credit for client:", invoice.clientId);
        try {
          await CreditManagementService.recalculateClientCredit(
            invoice.clientId
          );
          console.log("Client credit recalculated successfully");
        } catch (creditError) {
          console.error("Error recalculating client credit:", creditError);
          // Don't fail the status update, but log the error
        }
      }

      toast({
        title: t("invoices.statusUpdated"),
        description: t("invoices.statusUpdatedDescription", {
          status: t(`invoices.status.${newStatus}`),
        }),
      });
    } catch (error) {
      console.error("Error updating invoice status:", error);
      toast({
        title: t("common.error"),
        description: t("invoices.statusUpdateError"),
        variant: "destructive",
      });
    }
  };

  const handleQuickStatusChange = (invoice: Invoice) => {
    // Cycle through statuses: draft -> sent -> paid
    let nextStatus: string;
    switch (invoice.status) {
      case "draft":
        nextStatus = "sent";
        break;
      case "sent":
        nextStatus = "paid";
        break;
      default:
        // For paid, overdue, cancelled - no quick change
        return;
    }
    handleStatusChange(invoice, nextStatus);
  };

  const handleBulkStatusUpdate = async (status: string) => {
    if (selectedInvoices.size === 0) return;

    try {
      const updatePromises = Array.from(selectedInvoices).map((invoiceId) =>
        updateInvoice(invoiceId, { status: status as any })
      );
      await Promise.all(updatePromises);

      // Recalculate credit for all affected clients
      const affectedClientIds = new Set<string>();
      enhancedInvoices
        .filter(
          (invoice) => selectedInvoices.has(invoice.id) && invoice.clientId
        )
        .forEach((invoice) => {
          if (invoice.clientId) {
            affectedClientIds.add(invoice.clientId);
          }
        });

      if (affectedClientIds.size > 0) {
        console.log(
          "Recalculating credit for clients:",
          Array.from(affectedClientIds)
        );
        try {
          const creditPromises = Array.from(affectedClientIds).map((clientId) =>
            CreditManagementService.recalculateClientCredit(clientId)
          );
          await Promise.all(creditPromises);
          console.log("Bulk credit recalculation completed");
        } catch (creditError) {
          console.error("Error in bulk credit recalculation:", creditError);
          // Don't fail the status update, but log the error
        }
      }

      toast({
        title: t("invoices.bulkUpdateSuccess"),
        description: t("invoices.bulkUpdateSuccessDescription", {
          count: selectedInvoices.size,
          status: t(`invoices.status.${status}`),
        }),
      });

      setSelectedInvoices(new Set());
      setShowBulkActions(false);
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("invoices.bulkUpdateError"),
        variant: "destructive",
      });
    }
  };

  const handleBulkEmail = async () => {
    if (selectedInvoices.size === 0) return;

    // TODO: Implement bulk email functionality
    toast({
      title: t("common.info"),
      description: t("invoices.bulkEmailNotImplemented"),
    });
  };

  const handleSelectInvoice = (invoiceId: string, checked: boolean) => {
    const newSelected = new Set(selectedInvoices);
    if (checked) {
      newSelected.add(invoiceId);
    } else {
      newSelected.delete(invoiceId);
    }
    setSelectedInvoices(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredInvoices.map((inv) => inv.id));
      setSelectedInvoices(allIds);
      setShowBulkActions(allIds.size > 0);
    } else {
      setSelectedInvoices(new Set());
      setShowBulkActions(false);
    }
  };

  const handleUpdateOverdue = async () => {
    try {
      const updatedCount = await updateOverdueInvoices();
      toast({
        title: t("invoices.overdueUpdated"),
        description: t("invoices.overdueUpdatedDescription", {
          count: updatedCount,
        }),
      });
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("invoices.overdueUpdateError"),
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: EnhancedInvoice["status"]) => {
    const statusConfig = {
      draft: {
        variant: "secondary" as const,
        label: t("invoices.status.draft"),
      },
      sent: { variant: "default" as const, label: t("invoices.status.sent") },
      paid: {
        variant: "default" as const,
        label: t("invoices.status.paid"),
        className: "bg-green-500",
      },
      overdue: {
        variant: "destructive" as const,
        label: t("invoices.status.overdue"),
      },
      cancelled: {
        variant: "outline" as const,
        label: t("invoices.status.cancelled"),
      },
    };

    const config = statusConfig[status];
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const getPaymentStatusBadge = (
    paymentStatus: PaymentStatus,
    paidAmount: number,
    totalAmount: number
  ) => {
    const statusConfig = {
      unpaid: {
        variant: "secondary" as const,
        label: t("invoices.paymentStatus.unpaid"),
        icon: Clock,
      },
      partial: {
        variant: "default" as const,
        label: t("invoices.paymentStatus.partial"),
        className: "bg-yellow-500",
        icon: DollarSign,
      },
      paid: {
        variant: "default" as const,
        label: t("invoices.paymentStatus.paid"),
        className: "bg-green-500",
        icon: CheckSquare,
      },
      overpaid: {
        variant: "default" as const,
        label: t("invoices.paymentStatus.overpaid"),
        className: "bg-blue-500",
        icon: DollarSign,
      },
    };

    const config = statusConfig[paymentStatus];
    const Icon = config.icon;

    return (
      <div className="flex items-center gap-1">
        <Badge variant={config.variant} className={config.className}>
          <Icon className="h-3 w-3 mr-1" />
          {config.label}
        </Badge>
        {paymentStatus === "partial" && (
          <span className="text-xs text-gray-500">
            {formatCurrency(paidAmount)} / {formatCurrency(totalAmount)}
          </span>
        )}
      </div>
    );
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(3)} TND`;
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="invoices" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t("invoices.invoices")}
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {t("invoices.clients")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t("invoices.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Search and Filter Controls */}
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder={t("invoices.searchPlaceholder")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select
                    value={selectedStatus}
                    onValueChange={setSelectedStatus}
                  >
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder={t("invoices.selectStatus")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("common.all")}</SelectItem>
                      <SelectItem value="draft">
                        {t("invoices.status.draft")}
                      </SelectItem>
                      <SelectItem value="sent">
                        {t("invoices.status.sent")}
                      </SelectItem>
                      <SelectItem value="paid">
                        {t("invoices.status.paid")}
                      </SelectItem>
                      <SelectItem value="overdue">
                        {t("invoices.status.overdue")}
                      </SelectItem>
                      <SelectItem value="cancelled">
                        {t("invoices.status.cancelled")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={selectedPaymentStatus}
                    onValueChange={setSelectedPaymentStatus}
                  >
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue
                        placeholder={t("invoices.selectPaymentStatus")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("common.all")}</SelectItem>
                      <SelectItem value="unpaid">
                        {t("invoices.paymentStatus.unpaid")}
                      </SelectItem>
                      <SelectItem value="partial">
                        {t("invoices.paymentStatus.partial")}
                      </SelectItem>
                      <SelectItem value="paid">
                        {t("invoices.paymentStatus.paid")}
                      </SelectItem>
                      <SelectItem value="overpaid">
                        {t("invoices.paymentStatus.overpaid")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 justify-between">
                  <div className="flex gap-2">
                    <Button
                      onClick={handleCreateInvoice}
                      className="w-full sm:w-auto"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t("invoices.create")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleUpdateOverdue}
                      className="w-full sm:w-auto"
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      {t("invoices.updateOverdue")}
                    </Button>
                  </div>

                  {showBulkActions && (
                    <div className="flex gap-2">
                      <span className="text-sm text-gray-500 self-center">
                        {t("invoices.selectedCount", {
                          count: selectedInvoices.size,
                        })}
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            {t("invoices.bulkActions")}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem
                            onClick={() => handleBulkStatusUpdate("sent")}
                          >
                            <Send className="h-4 w-4 mr-2" />
                            {t("invoices.markAsSent")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleBulkStatusUpdate("paid")}
                          >
                            <CheckSquare className="h-4 w-4 mr-2" />
                            {t("invoices.markAsPaid")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleBulkEmail}>
                            <Mail className="h-4 w-4 mr-2" />
                            {t("invoices.sendBulkEmail")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              </div>

              {/* Invoices Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={
                            selectedInvoices.size === filteredInvoices.length &&
                            filteredInvoices.length > 0
                          }
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>{t("invoices.invoiceNumber")}</TableHead>
                      <TableHead>{t("invoices.client")}</TableHead>
                      <TableHead>{t("invoices.amount")}</TableHead>
                      <TableHead>{t("invoices.statusHeader")}</TableHead>
                      <TableHead>{t("invoices.paymentStatusHeader")}</TableHead>
                      <TableHead>{t("invoices.dueDate")}</TableHead>
                      <TableHead>{t("invoices.createdAt")}</TableHead>
                      <TableHead className="text-right">
                        {t("common.actions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading || enhancedLoading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8">
                          {t("common.loading")}
                        </TableCell>
                      </TableRow>
                    ) : filteredInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className="text-center py-8 text-gray-500"
                        >
                          {searchTerm ||
                          selectedStatus !== "all" ||
                          selectedPaymentStatus !== "all"
                            ? t("invoices.noResults")
                            : t("invoices.empty")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredInvoices.map((invoice) => (
                        <TableRow
                          key={invoice.id}
                          className={
                            selectedInvoices.has(invoice.id)
                              ? "bg-muted/50"
                              : ""
                          }
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedInvoices.has(invoice.id)}
                              onCheckedChange={(checked) =>
                                handleSelectInvoice(
                                  invoice.id,
                                  checked as boolean
                                )
                              }
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {invoice.invoiceNumber}
                            {invoice.isOverdue && (
                              <AlertTriangle className="h-4 w-4 text-red-500 inline ml-2" />
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {invoice.clientName}
                              </div>
                              {invoice.clientPhone && (
                                <div className="text-sm text-gray-500">
                                  {invoice.clientPhone}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">
                              {formatCurrency(invoice.totalAmount)}
                            </div>
                            {invoice.paymentStatus === "partial" && (
                              <div className="text-sm text-gray-500">
                                {t("invoices.remaining")}:{" "}
                                {formatCurrency(invoice.remainingAmount)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div
                              className="cursor-pointer"
                              onClick={() => handleQuickStatusChange(invoice)}
                              title={t("invoices.clickToChangeStatus")}
                            >
                              {getStatusBadge(invoice.status)}
                            </div>
                          </TableCell>
                          <TableCell>
                            {getPaymentStatusBadge(
                              invoice.paymentStatus,
                              invoice.paidAmount,
                              invoice.totalAmount
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              {invoice.dueDate
                                ? formatDate(invoice.dueDate)
                                : "-"}
                              {invoice.isOverdue && invoice.daysOverdue && (
                                <div className="text-sm text-red-500">
                                  {t("invoices.overdueBy", {
                                    days: invoice.daysOverdue,
                                  })}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(invoice.createdAt)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center gap-2 justify-end">
                              {invoice.paymentStatus !== "paid" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRecordPayment(invoice)}
                                >
                                  <DollarSign className="h-4 w-4 mr-1" />
                                  {t("invoices.recordPayment")}
                                </Button>
                              )}
                              <InvoicePDFDownload
                                invoice={invoice}
                                variant="preview"
                                size="sm"
                              />
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => handleViewInvoice(invoice)}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    {t("common.view")}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleEditInvoice(invoice)}
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    {t("common.edit")}
                                  </DropdownMenuItem>
                                  {invoice.status === "draft" && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleStatusChange(invoice, "sent")
                                      }
                                    >
                                      <Send className="h-4 w-4 mr-2" />
                                      {t("invoices.markAsSent")}
                                    </DropdownMenuItem>
                                  )}
                                  {invoice.status === "sent" &&
                                    invoice.paymentStatus !== "paid" && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          handleStatusChange(invoice, "paid")
                                        }
                                      >
                                        <CheckSquare className="h-4 w-4 mr-2" />
                                        {t("invoices.markAsPaid")}
                                      </DropdownMenuItem>
                                    )}
                                  {invoice.paymentStatus !== "paid" && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleRecordPayment(invoice)
                                      }
                                    >
                                      <DollarSign className="h-4 w-4 mr-2" />
                                      {t("invoices.recordPayment")}
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem asChild>
                                    <div className="w-full">
                                      <InvoicePDFDownload
                                        invoice={invoice}
                                        variant="print"
                                        size="sm"
                                        className="w-full justify-start p-0"
                                      />
                                    </div>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      toast({
                                        title: t("common.info"),
                                        description:
                                          "Email sending functionality will be implemented in the next task",
                                      });
                                    }}
                                  >
                                    <Send className="h-4 w-4 mr-2" />
                                    {t("invoices.sendEmail")}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteInvoice(invoice)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {t("common.delete")}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="clients" className="space-y-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">
                  {t("invoices.clientManagement")}
                </h2>
                <p className="text-gray-600">
                  {t("invoices.clientManagementDescription")}
                </p>
              </div>
            </div>
            <ClientManagement />
          </div>
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <InvoiceFormDialog
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingInvoice(null);
        }}
        invoice={editingInvoice}
        onSubmit={async (data) => {
          try {
            if (editingInvoice) {
              await updateInvoice(editingInvoice.id, data);
              toast({
                title: t("invoices.updated"),
                description: t("invoices.updatedDescription"),
              });
            } else {
              await createInvoice(data);
              toast({
                title: t("invoices.created"),
                description: t("invoices.createdDescription"),
              });
            }
            setIsFormOpen(false);
            setEditingInvoice(null);
          } catch (error) {
            toast({
              title: t("common.error"),
              description: editingInvoice
                ? t("invoices.updateError")
                : t("invoices.createError"),
              variant: "destructive",
            });
          }
        }}
      />

      {/* View Dialog */}
      <InvoiceViewDialog
        isOpen={isViewOpen}
        onClose={() => {
          setIsViewOpen(false);
          setViewingInvoice(null);
        }}
        invoice={viewingInvoice}
        onEdit={(invoice) => {
          setIsViewOpen(false);
          setViewingInvoice(null);
          handleEditInvoice(invoice);
        }}
        onDownloadPDF={(invoice) => {
          // TODO: Implement PDF download functionality
          toast({
            title: t("common.info"),
            description:
              "PDF download functionality will be implemented in the next task",
          });
        }}
        onSendEmail={(invoice) => {
          // TODO: Implement email sending functionality
          toast({
            title: t("common.info"),
            description:
              "Email sending functionality will be implemented in the next task",
          });
        }}
        onRecordPayment={(invoice) => {
          // TODO: Implement payment recording functionality
          toast({
            title: t("common.info"),
            description:
              "Payment recording functionality will be implemented in the next task",
          });
        }}
      />

      {/* Payment Record Form */}
      {paymentInvoice && (
        <PaymentRecordForm
          isOpen={isPaymentFormOpen}
          onClose={() => {
            setIsPaymentFormOpen(false);
            setPaymentInvoice(null);
          }}
          invoice={paymentInvoice}
          onSubmit={async (data) => {
            try {
              await createPaymentRecord(data);
              setIsPaymentFormOpen(false);
              setPaymentInvoice(null);
            } catch (error) {
              // Error handling is done in the context
            }
          }}
        />
      )}

      {/* Password Dialog */}
      <StockPasswordDialog
        open={isPasswordDialogOpen}
        onOpenChange={setIsPasswordDialogOpen}
        onConfirm={handlePasswordSuccess}
        title={t("common.securityVerification")}
        description={t("common.enterSecurityCodeToConfirm")}
        confirmButtonText={t("common.confirm")}
      />
    </div>
  );
};

export default InvoiceManagement;
